import { Component, Injector, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
    GenericApiServiceFactory,
    GenericBoundedApiService,
    LsqApiServiceListSource
} from '@lsq-frontend/angular-shared/api-service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { FastTrackApiRoutes } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ListUserStatesRequest,
    UserState,
    UserStateStatusDisplayEnum
} from '@lsq-frontend/remote-services/enterprise/common';
import { ListUserStatesService } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { forLifeOf, FormType, LifecycleAware, Modal } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { LsqCreateOrUpdateUserStateModalComponent } from './lsq-create-user-state-modal.component';

@LifecycleAware()
@Component({
    selector: 'user-state-management',
    template: `
        <lsq-banner [titleText]="'User State Management'"></lsq-banner>
        <div class="container-fluid page-container">
            <div class="row">
                <div class="col-12">
                    <lsq-button type="default" size="md" (buttonClick)="showCreateStateModal()">
                        Create User State
                    </lsq-button>
                </div>
                <div class="col-12">
                    <lsq-data-table
                        [dataSource]="dataSource"
                        [isPaginated]="true"
                        [hasFooter]="false"
                        [hasStickyHeader]="false"
                        [layoutFixed]="true"
                    >
                        <lsq-row-count-column></lsq-row-count-column>

                        <lsq-column
                            [column]="{
                                header: 'State Name',
                                key: 'userStateType',
                                sortable: false,
                                width: '30%'
                            }"
                        >
                        </lsq-column>

                        <lsq-column
                            [column]="{
                                header: 'Status',
                                key: 'userStateStatus',
                                sortable: false,
                                width: '10%'
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <div class="status-column">
                                    {{ userStateStatusDisplayEnum[row.userStateStatus] }}
                                </div>
                            </ng-template>
                        </lsq-column>

                        <lsq-column
                            [column]="{
                                header: 'JSON Data',
                                key: 'jsonData',
                                sortable: false,
                                truncateTextCss: true,
                                className: 'e2e-json-cell',
                                width: '50%'
                            }"
                        >
                        </lsq-column>

                        <lsq-column
                            [column]="{
                                header: 'Edit',
                                key: 'userStateKey',
                                sortable: false,
                                width: '5%'
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <a (click)="showModal(row)">
                                    <lsq-icon [type]="icons.Edit"></lsq-icon>
                                </a>
                            </ng-template>
                        </lsq-column>
                    </lsq-data-table>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['lsq-user-state-management.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqUserStateManagementComponent implements OnInit, OnDestroy {
    private _getUserStateService = this._genericApiServiceFactory.createBounded(
        `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.GetUserState}`
    );
    private _createOrUpdateUserStateService = this._genericApiServiceFactory.createBounded(
        `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.CreateOrUpdateUserState}`
    );
    private _listUserStatesRequest = new ListUserStatesRequest();

    public listUserStatesService: GenericBoundedApiService;
    public dataSource = new LsqApiServiceListSource(
        this._listUserStatesService,
        this._listUserStatesRequest
    );
    public icons = Icons;
    public userStateStatusDisplayEnum = UserStateStatusDisplayEnum;

    constructor(
        private _genericApiServiceFactory: GenericApiServiceFactory,
        private _modalService: ModalService,
        private _alertService: AlertService,
        private _listUserStatesService: ListUserStatesService,
        public injector: Injector
    ) {}

    public ngOnInit(): void {
        this._createOrUpdateUserStateService.dataStream.pipe(forLifeOf(this)).subscribe(() => {
            this._modalService.closeModal();
            this._alertService.success('User State Successfully Created/Updated');
            this.dataSource.dispatchRequest(new ListUserStatesRequest());
        });
    }

    public ngOnDestroy(): void {}

    public showCreateStateModal(): void {
        this._modalService.showModal(
            new Modal({
                id: 'add-user-state-modal',
                title: 'Create New User State',
                width: '71.5rem',
                bodyComponent: LsqCreateOrUpdateUserStateModalComponent,
                showCloseButton: true,
                className: 'user-state-modal',
                data: {
                    requestType: FormType.Create,
                    createOrUpdateUserStateService: this._createOrUpdateUserStateService,
                    getUserStateService: this._getUserStateService
                }
            })
        );
    }

    public showModal(userState: UserState): void {
        this._modalService.showModal(
            new Modal({
                id: 'update-user-state-modal',
                title: 'Edit User State',
                width: '71.5rem',
                bodyComponent: LsqCreateOrUpdateUserStateModalComponent,
                showCloseButton: true,
                className: 'user-state-modal',
                data: {
                    requestType: FormType.Update,
                    userState,
                    createOrUpdateUserStateService: this._createOrUpdateUserStateService,
                    getUserStateService: this._getUserStateService
                }
            })
        );
    }
}
